import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import "./admin.css";
import { loadAssets } from "./game/assets";
import { Screen, decodeFont } from "./game/screen";
import { IO } from "./game/io";
import { Sound } from "./game/sound";
import { Game, showHighScoresView } from "./game/logic";
import { hasSave, loadSettings, saveSettings } from "./game/save";
import { getPlayerName, setPlayerName, startSession, recordGame } from "./game/session";
import { useGameKeyboard } from "./useGameKeyboard";
import { NameScreen } from "./NameScreen";
import { DedicationScreen } from "./DedicationScreen";
import { TitleScreen } from "./TitleScreen";
import { GameControls } from "./GameControls";
import Admin from "./Admin";
import Settings from "./Settings";
import ProfitHelper from "./ProfitHelper";

const ROTATE_KEY = "socher_hayam_rotate_v1";

export default function App() {
  const canvasRef = useRef(null);
  const screenRef = useRef(null);
  const ioRef = useRef(null);
  const soundRef = useRef(null);
  const gameRef = useRef(null);
  // loading | name | dedication | title | playing | admin
  const [phase, setPhase] = useState("loading");
  const [controls, setControls] = useState({ mode: null });
  const [soundOn, setSoundOn] = useState(() => loadSettings().soundOn !== false);
  const [saveExists, setSaveExists] = useState(false);
  const [crashMsg, setCrashMsg] = useState("");
  const [nameVal, setNameVal] = useState("");
  const [playerName, setPlayerNameVal] = useState(() => getPlayerName());
  const [playerNameErr, setPlayerNameErr] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [profitData, setProfitData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loadProgress, setLoadProgress] = useState("");
  const [dims, setDims] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [forceLandscape, setForceLandscape] = useState(() => {
    try {
      return localStorage.getItem(ROTATE_KEY) === "1";
    } catch (e) {
      console.warn("localStorage unavailable", e);
      return false;
    }
  });

  const showSettingsRef = useRef(showSettings);
  showSettingsRef.current = showSettings || !!profitData;
  const controlsRef = useRef(controls);
  controlsRef.current = controls;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const deviceLandscape = dims.w > dims.h;
  const isDesktop = dims.w >= 1024;
  const uiLandscape = (deviceLandscape && (dims.h <= 580 || isDesktop)) || (forceLandscape && !deviceLandscape);
  const rotated = forceLandscape && !deviceLandscape;
  const rotatedRef = useRef(rotated);
  rotatedRef.current = rotated;

  useEffect(() => {
    const onResize = () => setDims({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const toggleForceLandscape = useCallback(() => {
    setForceLandscape((f) => {
      const nf = !f;
      try {
        localStorage.setItem(ROTATE_KEY, nf ? "1" : "0");
      } catch (e) {
        console.warn("failed to persist rotate preference", e);
      }
      (async () => {
        try {
          if (nf) {
            // native lock where supported (Android Chrome requires fullscreen);
            // elsewhere the CSS rotation fallback takes over
            if (document.documentElement.requestFullscreen) await document.documentElement.requestFullscreen();
            if (window.screen.orientation && window.screen.orientation.lock) {
              await window.screen.orientation.lock("landscape");
            }
          } else {
            if (window.screen.orientation && window.screen.orientation.unlock) window.screen.orientation.unlock();
            if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
          }
        } catch (e) {
          console.debug("native orientation lock unavailable, CSS fallback active", e);
        }
      })();
      return nf;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const assets = await loadAssets((done, total) => setLoadProgress(`${done}/${total}`));
        if (cancelled) return;
        const font = decodeFont();
        const sound = new Sound();
        sound.enabled = loadSettings().soundOn !== false;
        const screen = new Screen(canvasRef.current, assets, font);
        const io = new IO(screen, sound, (c) => setControls(c));
        screen.putPic("mifne_scr", 0, 199);
        screenRef.current = screen;
        ioRef.current = io;
        soundRef.current = sound;
        setSaveExists(hasSave());
        setPhase("name");
      } catch (e) {
        console.error("load failed", e);
        setLoadError(String(e && e.message ? e.message : e));
      }
    })();
    return () => {
      cancelled = true;
      if (ioRef.current) ioRef.current.abort();
    };
  }, []);

  const handleCanvasPointer = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas || !ioRef.current) return;
    if (soundRef.current) soundRef.current.ensure();
    const rect = canvas.getBoundingClientRect();
    let x;
    let y;
    if (rotatedRef.current) {
      // canvas is rotated 90deg clockwise via CSS - remap viewport coords
      x = Math.floor(((e.clientY - rect.top) / rect.height) * 320);
      y = Math.floor(((rect.right - e.clientX) / rect.width) * 200);
    } else {
      x = Math.floor(((e.clientX - rect.left) / rect.width) * 320);
      y = Math.floor(((e.clientY - rect.top) / rect.height) * 200);
    }
    ioRef.current.handleCanvasTap(x, y);
  }, []);

  const backToTitle = useCallback(() => {
    ioRef.current.aborted = false;
    setSaveExists(hasSave());
    screenRef.current.putPic("mifne_scr", 0, 199);
    setControls({ mode: null });
    setPhase("title");
  }, []);

  const startGame = useCallback(
    (cont) => {
      if (soundRef.current) soundRef.current.ensure();
      ioRef.current.aborted = false;
      recordGame();
      const game = new Game(screenRef.current, ioRef.current, soundRef.current);
      gameRef.current = game;
      window.__game = game;
      setCrashMsg("");
      setPhase("playing");
      game
        .run(cont)
        .catch((e) => {
          const msg = e && e.stack ? e.stack : String(e);
          console.error("game crashed:", msg);
          try {
            localStorage.setItem("socher_hayam_last_error", msg.slice(0, 800));
          } catch (err) {
            console.warn("failed to store crash details", err);
          }
          setCrashMsg("המשחק נתקל בתקלה ושוחזר. ההתקדמות שלך נשמרה - אפשר להמשיך מאותה נקודה.");
        })
        .finally(backToTitle);
    },
    [backToTitle]
  );

  const showScores = useCallback(() => {
    if (soundRef.current) soundRef.current.ensure();
    ioRef.current.aborted = false;
    setPhase("playing");
    showHighScoresView(screenRef.current, ioRef.current).catch(() => {}).finally(backToTitle);
  }, [backToTitle]);

  const exitGame = useCallback(() => {
    // progress is autosaved every game morning
    if (window.confirm("לצאת מהמשחק? ההתקדמות נשמרה מבוקר היום האחרון.")) {
      ioRef.current.abort();
    }
  }, []);

  const toggleSound = useCallback(() => {
    setSoundOn((on) => {
      const next = !on;
      soundRef.current.enabled = next;
      // persist so the Settings panel and reloads stay in sync
      try {
        const s = loadSettings();
        saveSettings({ ...s, soundOn: next });
        if (gameRef.current) gameRef.current.settings = { ...gameRef.current.settings, soundOn: next };
      } catch (e) {
        console.warn("failed to persist sound preference", e);
      }
      return next;
    });
  }, []);

  const submitPlayerName = useCallback(() => {
    const n = playerName.trim().slice(0, 16);
    if (!n) {
      setPlayerNameErr(true);
      return;
    }
    setPlayerName(n);
    setPlayerNameVal(n);
    startSession(n);
    setPhase("dedication");
  }, [playerName]);

  const changePlayerName = useCallback((v) => {
    setPlayerNameVal(v);
    setPlayerNameErr(false);
  }, []);

  const newGame = useCallback(() => startGame(false), [startGame]);
  const continueGame = useCallback(() => startGame(true), [startGame]);
  const goTitle = useCallback(() => setPhase("title"), []);
  const goDedication = useCallback(() => setPhase("dedication"), []);
  const goAdmin = useCallback(() => setPhase("admin"), []);
  const openSettings = useCallback(() => setShowSettings(true), []);
  const closeSettings = useCallback(() => setShowSettings(false), []);
  const openProfitHelper = useCallback(() => {
    if (gameRef.current) setProfitData(gameRef.current.getProfitSnapshot());
  }, []);
  const closeProfitHelper = useCallback(() => setProfitData(null), []);
  const applySettings = useCallback((st) => {
    // apply live to a running game; new games load the saved settings themselves
    if (gameRef.current) gameRef.current.settings = { ...st };
    // sound is a UI-level setting, sync the audio engine + topbar icon
    if (soundRef.current) soundRef.current.enabled = st.soundOn !== false;
    setSoundOn(st.soundOn !== false);
  }, []);

  // prefill the high-score name input with the player's name
  useEffect(() => {
    if (controls.mode === "name") {
      const n = getPlayerName();
      if (n) {
        setNameVal(n);
        if (ioRef.current) ioRef.current.nameChanged(n);
      }
    }
  }, [controls.mode]);

  useGameKeyboard({ ioRef, soundRef, phaseRef, controlsRef, showSettingsRef, startGame });

  // In landscape the canvas size is computed here to keep the exact
  // 320x200 aspect (CSS alone can't satisfy both height and width caps)
  let wrapStyle;
  if (uiLandscape) {
    const availH = rotated ? dims.w : dims.h;
    const availW = (rotated ? dims.h : dims.w) - (isDesktop ? 280 : 215);
    const w = Math.max(200, Math.min(availH * 1.6, availW));
    wrapStyle = { width: `${w}px`, height: `${w / 1.6}px` };
  }

  const io = ioRef.current;

  return (
    <div className={"app" + (uiLandscape ? " landscape" : "") + (rotated ? " rotated" : "")} dir="rtl">
      <div className="frame">
        <div className="screen-wrap" style={wrapStyle}>
          <canvas
            ref={canvasRef}
            width={320}
            height={200}
            className="game-canvas"
            data-testid="game-canvas"
            onPointerDown={handleCanvasPointer}
          />
          <div className="scanlines" />
          {phase === "loading" && (
            <div className="overlay" data-testid="loading-overlay">
              <div className="loading-text">{loadError ? `שגיאה: ${loadError}` : `...טוען את סוחר הים ${loadProgress}`}</div>
            </div>
          )}
        </div>

        {phase === "title" && (
          <TitleScreen
            saveExists={saveExists}
            forceLandscape={forceLandscape}
            crashMsg={crashMsg}
            onNewGame={newGame}
            onContinue={continueGame}
            onScores={showScores}
            onSettings={openSettings}
            onRotate={toggleForceLandscape}
            onDedication={goDedication}
            onAdmin={goAdmin}
          />
        )}

        {phase === "name" && (
          <NameScreen value={playerName} error={playerNameErr} onChange={changePlayerName} onSubmit={submitPlayerName} />
        )}

        {phase === "dedication" && <DedicationScreen onContinue={goTitle} />}

        {phase === "admin" && <Admin onBack={goTitle} />}

        {showSettings && <Settings onClose={closeSettings} onSaved={applySettings} />}
        {profitData && <ProfitHelper data={profitData} onClose={closeProfitHelper} />}

        {phase === "playing" && (
          <div className="side-panel">
            <div className="topbar">
              <button className="ibtn" data-testid="exit-btn" onClick={exitGame} title="יציאה">
                ✕
              </button>
              <div className="topbar-title">סוחר הים</div>
              <div className="topbar-actions">
                <button className="ibtn" data-testid="settings-btn" onClick={openSettings} title="הגדרות">
                  ⚙
                </button>
                <button
                  className={`ibtn${forceLandscape ? " active" : ""}`}
                  data-testid="rotate-btn"
                  onClick={toggleForceLandscape}
                  title="סיבוב מסך"
                >
                  ⤢
                </button>
                <button className="ibtn" data-testid="sound-btn" onClick={toggleSound} title="צליל">
                  {soundOn ? "♪" : "∅"}
                </button>
                {io && io.helpAvailable && (
                  <button className="ibtn" data-testid="help-btn" onClick={() => io.toggleHelp()} title="מצב הספינה">
                    ?
                  </button>
                )}
              </div>
            </div>
            <GameControls
              io={io}
              controls={controls}
              nameVal={nameVal}
              setNameVal={setNameVal}
              onProfit={openProfitHelper}
            />
          </div>
        )}
      </div>
    </div>
  );
}
